import apiClient from "../../api/apiClient";

export interface Company {
  id: number;
  company_name: string;
  company_type: string;
  industry?: string;
  establishment_date?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  tax_id?: string;
  company_size?: string;
  annual_revenue?: number;
  logo_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  managed_companies?: number[];
  managing_agencies?: number[];
}

export interface CompanyCreate {
  company_name: string;
  company_type: string;
  industry?: string;
  establishment_date?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  tax_id?: string;
  company_size?: string;
  annual_revenue?: number;
  logo_url?: string;
  is_active?: boolean;
  managed_company_ids?: number[];
}

export interface CompanyUpdate {
  company_name?: string;
  company_type?: string;
  industry?: string;
  establishment_date?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  tax_id?: string;
  company_size?: string;
  annual_revenue?: number;
  logo_url?: string;
  is_active?: boolean;
  managed_company_ids?: number[];
}

export interface CompanyFilters {
  company_type?: string;
  is_active?: boolean;
  agency_id?: number;
  search?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface AgencyClientRelation {
  agency_id: number;
  managed_company_id: number;
}

export interface CompanyUserRelation {
  user_ids: number[];
  role?: string;
}

const COMPANIES_ENDPOINT = "/companies";

// Şirket servisi
const companiesService = {
  // Tüm şirketleri getir
  getCompanies: async (
    params?: CompanyFilters
  ): Promise<{ companies: Company[]; total: number }> => {
    try {

      // Filtre parametrelerini oluştur
      const queryParams = new URLSearchParams();

      // Sayfalama parametrelerini ekle
      if (params?.page !== undefined) {
        const skip = (params.page - 1) * (params.limit || 10);
        queryParams.append("skip", skip.toString());
      }

      if (params?.limit !== undefined) {
        queryParams.append("limit", params.limit.toString());
      }

      // Diğer filtreleri ekle
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (
            value !== undefined &&
            value !== null &&
            !["page", "limit"].includes(key)
          ) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const queryString = queryParams.toString()
        ? `?${queryParams.toString()}`
        : "";
      const response = await apiClient.get(
        `${COMPANIES_ENDPOINT}${queryString}`
      );

      // Backend artık { companies: [], total: number } formatında dönüyor
      let companies = [];
      let total = 0;

      if (response && typeof response === "object") {
        if ("companies" in response && "total" in response) {
          companies = response.companies;
          total = response.total;
        } else {
          // Fallback: Eğer eski format dönüyorsa
          companies = Array.isArray(response) ? response : [];
          total = companies.length;
        }
      }

      return { companies, total };
    } catch (error) {
      console.error("Error fetching companies:", error);
      return { companies: [], total: 0 };
    }
  },

  // Şirket detaylarını getir
  getCompany: async (id: number): Promise<Company> => {
    try {
      const response = await apiClient.get(`${COMPANIES_ENDPOINT}/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching company with ID ${id}:`, error);
      throw error;
    }
  },

  // Yeni şirket oluştur
  createCompany: async (companyData: CompanyCreate): Promise<Company> => {
    try {
      const response = await apiClient.post(
        `${COMPANIES_ENDPOINT}`,
        companyData
      );
      return response;
    } catch (error) {
      console.error("Error creating company:", error);
      throw error;
    }
  },

  // Şirket bilgilerini güncelle
  updateCompany: async (
    id: number,
    companyData: CompanyUpdate
  ): Promise<Company> => {
    try {
      const response = await apiClient.put(
        `${COMPANIES_ENDPOINT}/${id}`,
        companyData
      );
      return response;
    } catch (error) {
      console.error(`Error updating company with ID ${id}:`, error);
      throw error;
    }
  },

  // Şirketi sil
  deleteCompany: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`${COMPANIES_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting company with ID ${id}:`, error);
      throw error;
    }
  },

  // Şirket kullanıcılarını getir
  getCompanyUsers: async (companyId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(
        `${COMPANIES_ENDPOINT}/${companyId}/users`
      );
      return response;
    } catch (error) {
      console.error(
        `Error fetching users for company with ID ${companyId}:`,
        error
      );
      throw error;
    }
  },

  // Şirkete kullanıcı ekle
  addUsersToCompany: async (
    companyId: number,
    userRelation: CompanyUserRelation
  ): Promise<void> => {
    try {
      await apiClient.patch(
        `${COMPANIES_ENDPOINT}/${companyId}/users`,
        userRelation
      );
    } catch (error) {
      console.error(
        `Error adding users to company with ID ${companyId}:`,
        error
      );
      throw error;
    }
  },

  // Şirketten kullanıcı çıkar
  removeUsersFromCompany: async (
    companyId: number,
    userRelation: CompanyUserRelation
  ): Promise<void> => {
    try {
      await apiClient.delete(
        `${COMPANIES_ENDPOINT}/${companyId}/users`,
        { data: userRelation }
      );
    } catch (error) {
      console.error(
        `Error removing users from company with ID ${companyId}:`,
        error
      );
      throw error;
    }
  },

  // Ajans-müşteri ilişkisi ekle
  addAgencyClientRelation: async (
    relation: AgencyClientRelation
  ): Promise<void> => {
    try {
      await apiClient.post(`${COMPANIES_ENDPOINT}/agency-client`, relation);
    } catch (error) {
      console.error(`Error adding agency-client relation:`, error);
      throw error;
    }
  },

  // Ajans-müşteri ilişkisini kaldır
  removeAgencyClientRelation: async (
    relation: AgencyClientRelation
  ): Promise<void> => {
    try {
      await apiClient.delete(
        `${COMPANIES_ENDPOINT}/agency-client`,
        { data: relation }
      );
    } catch (error) {
      console.error(`Error removing agency-client relation:`, error);
      throw error;
    }
  },

  getClientsByAgencyId: async (agencyId: number) => {
    try {
      // Düzeltilmiş endpoint - doğru yolu kullanıyoruz
      const response = await apiClient.get(
        `${COMPANIES_ENDPOINT}/client-companies/agency/${agencyId}`
      );
      return response || [];
    } catch (error) {
      console.error(`Error fetching clients for agency ID ${agencyId}:`, error);
      return []; // Hata durumunda boş dizi döndür
    }
  },

  // Agency Owner için kullanıcı atanabilecek şirketleri getir
  getAssignableCompanies: async () => {
    try {
      const response = await apiClient.get(`${COMPANIES_ENDPOINT}`);
      return response || [];
    } catch (error) {
      console.error("Error fetching assignable companies:", error);
      return [];
    }
  },

  // Agency Owner'ın kendi agency'sini getir
  getMyAgency: async (): Promise<Company> => {
    try {
      const response = await apiClient.get(`${COMPANIES_ENDPOINT}/my-agency`);
      return response;
    } catch (error) {
      console.error("Error fetching my agency:", error);
      throw error;
    }
  },

  // Company Owner'ın kendi single şirketini getir
  getMyCompany: async (): Promise<Company> => {
    try {
      const response = await apiClient.get(`${COMPANIES_ENDPOINT}/my-company`);
      return response;
    } catch (error) {
      console.error("Error fetching my company:", error);
      throw error;
    }
  },

  // Agency Owner'ın kendi agency'sini güncelle
  updateMyAgency: async (companyData: CompanyUpdate): Promise<Company> => {
    try {
      const response = await apiClient.put(
        `${COMPANIES_ENDPOINT}/my-agency`,
        companyData
      );
      return response;
    } catch (error) {
      console.error("Error updating my agency:", error);
      throw error;
    }
  },

  // Company Owner'ın kendi single şirketini güncelle
  updateMyCompany: async (companyData: CompanyUpdate): Promise<Company> => {
    try {
      const response = await apiClient.put(
        `${COMPANIES_ENDPOINT}/my-company`,
        companyData
      );
      return response;
    } catch (error) {
      console.error("Error updating my company:", error);
      throw error;
    }
  },

  // Agency Owner'ın client companies'lerini getir
  getMyClientCompanies: async (
    params?: { skip?: number; limit?: number }
  ): Promise<Company[]> => {
    try {

      const queryParams = new URLSearchParams();
      if (params?.skip !== undefined) {
        queryParams.append("skip", params.skip.toString());
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", params.limit.toString());
      }

      const queryString = queryParams.toString()
        ? `?${queryParams.toString()}`
        : "";

      const response = await apiClient.get(
        `${COMPANIES_ENDPOINT}/my-client-companies${queryString}`
      );
      return response || [];
    } catch (error) {
      console.error("Error fetching my client companies:", error);
      return [];
    }
  },

  // Agency Owner'ın agency'sini ve yönettiği tüm şirketleri getir
  getMyAgencyCompanies: async (): Promise<Company[]> => {
    try {
      const response = await apiClient.get(`${COMPANIES_ENDPOINT}/my-agency-companies`);
      return response || [];
    } catch (error) {
      console.error("Error fetching my agency companies:", error);
      return [];
    }
  },
};

export default companiesService;
